import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Button, Card, message, Modal, Space, Row, Col, Switch, Progress, List, Tag } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { DownloadOutlined } from '@ant-design/icons';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const SENDER_INFO_KEY = 'sender_info_cache';

const PlaceOrder = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [useCachedSender, setUseCachedSender] = useState(true);
  const { user } = useAuth();
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [batchOrders, setBatchOrders] = useState([]);
  const [totalProgress, setTotalProgress] = useState(0);
  const [batchSize, setBatchSize] = useState(1);

  // 加载缓存的发件人信息
  useEffect(() => {
    const cachedSender = localStorage.getItem(SENDER_INFO_KEY);
    if (cachedSender && useCachedSender) {
      try {
        const senderInfo = JSON.parse(cachedSender);
        form.setFieldsValue({
          sender_name: senderInfo.sender_name,
          sender_phone: senderInfo.sender_phone,
          sender_street: senderInfo.sender_street,
          sender_num: senderInfo.sender_num,
          sender_zip: senderInfo.sender_zip,
          sender_city: senderInfo.sender_city,
        });
      } catch (error) {
        console.error('加载发件人信息失败:', error);
      }
    }
  }, [form, useCachedSender]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/orders/create`, values, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.code === 10000) {
        message.success('下单成功');
        setPdfUrl(response.data.data.pdf);
        
        // 保存发件人信息到缓存
        if (useCachedSender) {
          const senderInfo = {
            sender_name: values.sender_name,
            sender_phone: values.sender_phone,
            sender_street: values.sender_street,
            sender_num: values.sender_num,
            sender_zip: values.sender_zip,
            sender_city: values.sender_city,
          };
          localStorage.setItem(SENDER_INFO_KEY, JSON.stringify(senderInfo));
        }
        
        form.resetFields();
      } else {
        message.error(response.data.msg || '下单失败');
      }
    } catch (error) {
      message.error('下单失败：' + (error.response?.data?.detail || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  const handleUseCachedSenderChange = (checked) => {
    setUseCachedSender(checked);
    if (!checked) {
      // 清除缓存的发件人信息
      localStorage.removeItem(SENDER_INFO_KEY);
      // 清空表单中的发件人信息
      form.setFieldsValue({
        sender_name: undefined,
        sender_phone: undefined,
        sender_street: undefined,
        sender_num: undefined,
        sender_zip: undefined,
        sender_city: undefined,
      });
    } else {
      // 重新加载缓存的发件人信息
      const cachedSender = localStorage.getItem(SENDER_INFO_KEY);
      if (cachedSender) {
        try {
          const senderInfo = JSON.parse(cachedSender);
          form.setFieldsValue(senderInfo);
        } catch (error) {
          console.error('加载发件人信息失败:', error);
        }
      }
    }
  };

  const handleBatchOrder = async (values) => {
    setBatchModalVisible(true);
    setBatchOrders([]);
    setTotalProgress(0);
    setLoading(true);

    const token = localStorage.getItem('token');
    const basePid = values.pid;
    const totalOrders = Math.min(batchSize, 20);
    const completedOrders = [];

    try {
      for (let i = 1; i <= totalOrders; i++) {
        const orderValues = {
          ...values,
          pid: `${basePid}-${i}`
        };

        try {
          const response = await axios.post(
            `${API_BASE_URL}/orders/create`,
            orderValues,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );

          if (response.data.code === 10000) {
            completedOrders.push({
              pid: orderValues.pid,
              fullcode: response.data.data?.fullcode,
              pdf: response.data.data?.pdf,
              status: 'success',
              message: '下单成功'
            });
          } else {
            completedOrders.push({
              pid: orderValues.pid,
              status: 'error',
              message: response.data.msg || '下单失败'
            });
          }
        } catch (error) {
          completedOrders.push({
            pid: orderValues.pid,
            status: 'error',
            message: error.response?.data?.detail || '未知错误'
          });
        }

        setBatchOrders([...completedOrders]);
        setTotalProgress((i / totalOrders) * 100);
      }

      if (completedOrders.every(order => order.status === 'success')) {
        message.success('批量下单完成');
        form.resetFields();
      } else {
        message.warning('部分订单下单失败，请查看详情');
      }
    } catch (error) {
      message.error('批量下单过程出错：' + (error.response?.data?.detail || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = (url) => {
    if (url) {
      window.open(url, '_blank');
    } else {
      message.error('PDF文件不存在');
    }
  };

  return (
    <Card 
      title="创建订单"
      headStyle={{ 
        background: '#1890ff',
        color: '#fff',
        padding: '16px 24px',
        fontSize: '18px',
        fontWeight: 'bold'
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          sender_phone: "000000000",
          receiver_phone: "000000000"
        }}
      >
        <Form.Item
          name="pid"
          label="参考单号"
          rules={[{ required: true, message: '请输入参考单号' }]}
        >
          <Input />
        </Form.Item>

        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <Card 
              title="发件人信息" 
              size="small"
              headStyle={{ 
                background: '#f0f2f5',
                padding: '12px 16px',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
              extra={
                <Space>
                  <Switch
                    checked={useCachedSender}
                    onChange={handleUseCachedSenderChange}
                    checkedChildren="使用缓存"
                    unCheckedChildren="不使用缓存"
                  />
                </Space>
              }
            >
              <Form.Item
                name="sender_name"
                label="发件人姓名"
                rules={[{ required: true, message: '请输入发件人姓名' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="sender_phone"
                label="发件人电话"
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="sender_street"
                label="发件人街道"
                rules={[{ required: true, message: '请输入发件人街道' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="sender_num"
                label="发件人门牌号"
                rules={[{ required: true, message: '请输入发件人门牌号' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="sender_zip"
                label="发件人邮编"
                rules={[{ required: true, message: '请输入发件人邮编' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="sender_city"
                label="发件人城市"
                rules={[{ required: true, message: '请输入发件人城市' }]}
              >
                <Input />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <Card 
              title="收件人信息" 
              size="small"
              headStyle={{ 
                background: '#f0f2f5',
                padding: '12px 16px',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              <Form.Item
                name="receiver_name"
                label="收件人姓名"
                rules={[{ required: true, message: '请输入收件人姓名' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="receiver_phone"
                label="收件人电话"
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="receiver_street"
                label="收件人街道"
                rules={[{ required: true, message: '请输入收件人街道' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="receiver_num"
                label="收件人门牌号"
                rules={[{ required: true, message: '请输入收件人门牌号' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="receiver_zip"
                label="收件人邮编"
                rules={[{ required: true, message: '请输入收件人邮编' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="receiver_city"
                label="收件人城市"
                rules={[{ required: true, message: '请输入收件人城市' }]}
              >
                <Input />
              </Form.Item>
            </Card>
          </Col>
        </Row>

        <Form.Item
          name="pack_weight"
          label="包裹重量(kg)"
          rules={[{ required: true, message: '请输入包裹重量' }]}
        >
          <InputNumber min={0.01} max={999.99} step={0.01} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="batch_size"
          label="批量下单数量"
          rules={[{ required: true, message: '请输入批量下单数量' }]}
        >
          <InputNumber
            min={1}
            max={40}
            defaultValue={1}
            onChange={setBatchSize}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading} 
              block
              disabled={batchSize > 1}
            >
              提交订单
            </Button>
            <Button 
              type="primary" 
              onClick={() => form.validateFields().then(handleBatchOrder)}
              loading={loading}
              disabled={batchSize === 1}
            >
              批量下单
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Modal
        title="下单成功"
        open={!!pdfUrl}
        onOk={handleDownloadPDF}
        onCancel={() => setPdfUrl(null)}
      >
        <p>订单创建成功！</p>
        <p>剩余API额度：{user?.api_quota || 0}</p>
        <p>是否下载PDF标签？</p>
      </Modal>

      <Modal
        title="批量下单进度"
        open={batchModalVisible}
        onCancel={() => setBatchModalVisible(false)}
        footer={null}
        width={800}
      >
        <Progress percent={totalProgress} status={totalProgress === 100 ? 'success' : 'active'} />
        <List
          dataSource={batchOrders}
          renderItem={item => (
            <List.Item
              actions={[
                item.status === 'success' && item.pdf && (
                  <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadPdf(item.pdf)}
                  >
                    下载PDF
                  </Button>
                )
              ]}
            >
              <List.Item.Meta
                title={item.pid}
                description={
                  <Space>
                    <Tag color={item.status === 'success' ? 'green' : 'red'}>
                      {item.status === 'success' ? '成功' : '失败'}
                    </Tag>
                    <span>{item.message}</span>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Modal>
    </Card>
  );
};

export default PlaceOrder;